import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree} from '@angular/router';
import { forkJoin, Observable, of, catchError, map, tap } from 'rxjs';
import { CampaignFacadeService } from '../facades/campaign.facade.service';
import { Campaign, CampaignInfo, CampaignTypes, LiteCampaignTypes } from '../models/campaign.model';
import { AuthService } from '../services/auth.service';
import { CampaignStateService } from '../services/campaign-state.service';
import { CampaignService } from '../services/campaign.service';
import { CustomNavigationService } from '../custom-navigation.service';

@Injectable({
  providedIn: 'root'
})
export class CampaignGuard  {
  guid!: string | null;
  kioskId: string  = null;
  uwId: string = null;

  constructor(private campaignService: CampaignService,
    private customNavigationService: CustomNavigationService,
    private campaignStateService: CampaignStateService,
    private campaignFacadeService: CampaignFacadeService,
    private authService: AuthService,
   ){
   }

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

       if ( this.campaignService.campaignData) {
         return true;
       }
    //this.guid = route.queryParams.cguid;
    this.guid = route.paramMap.get("cguid");
    localStorage.setItem('current-campaign', this.guid);

    if (!this.guid) {
      // Access parent route's ActivatedRoute
      const parentRoute = route.parent;

      if (parentRoute) {
        // Access parent route's paramMap
        this.guid = parentRoute.paramMap.get('cguid');
      }
    }

    this.kioskId = route.queryParams.kiosk;
    this.uwId = route.queryParams.uw;

    localStorage.setItem('current-kiosk', this.kioskId);

    localStorage.setItem('current-uw', this.uwId);

    if (!this.guid) {
      this.redirectUser();
    }

    return  this.loadCampaingFromQueryString().pipe(map(data=>{
        if (data && data.campaign && data.campaign.info){
          return true;
        } else {
          return false;
        }
      }));

  }

  loadCampaingFromQueryString(): Observable<{campaign: Campaign | null, campaignList: CampaignInfo[]}>{
      if (this.guid) {

        let obs$;

        if (this.authService.isUserLoggedId()) {
          obs$ = forkJoin([
            this.campaignService.getCampaingInfo(this.guid),
            this.campaignService.getDonorActiveCampaignList(),
            this.campaignService.getLatestClosedCampaignGuid()
          ]).pipe(
            map((data) => {
              const campaign = data[0];
              const campaignList = data[1];
              const latestClosedCampaignGuid = data[2];
              return { campaign, campaignList, latestClosedCampaignGuid };
            })
          );
        } else {
          obs$ = this.campaignService.getCampaingInfo(this.guid).pipe(map(campaign => {
            return {campaign, campaignList: null as CampaignInfo[] | null, latestClosedCampaignGuid: null as string | null};
          }))
        }

       return obs$.pipe(
         tap(
           (data: {
             campaign: Campaign,
             campaignList: CampaignInfo[] | null,
             latestClosedCampaignGuid: string | null
           }) => {
             const camp = data.campaign as Campaign;
             const campaignList = data.campaignList as CampaignInfo[];
             const latestClosedCampaignGuid = data.latestClosedCampaignGuid;

             // if regular campaign and the user is not logged in, re-direct user to login
             if (
               camp.info.campaignType == CampaignTypes.Regular &&
               !this.authService.isUserLoggedId()
             ) {
               this.customNavigationService.navigate(['/main', 'login']);
               return;
             }

             if (campaignList && campaignList.length > 0) {

              if (
                camp.info.campaignType == CampaignTypes.Regular
              ) {
                const cmpIdx = campaignList.findIndex(
                  (c) => c.campaignId == camp.info.campaignId
                );

                if (cmpIdx == -1) {
                  this.customNavigationService.navigate(['/donor-info']);
                  return;
                }
              }

             } else if (latestClosedCampaignGuid) {
                if (camp.info.campaignGuid != latestClosedCampaignGuid) {
                  this.customNavigationService.navigate(['/donor-info']);
                  return;
                }
             }

             this.campaignStateService.setSelectedCampaign(camp.info);
             this.campaignFacadeService.setCurrentCampaign(camp.info);

             if(!camp.type.isCampaignClosed) {
              this.redirectForLightCampaing(camp.info);
             }
             
             return;
           }
         ),
         catchError(( error: any) => {
           this.redirectUser();
           return of(null);
         })
       );
      } else {
        this.redirectUser();
        return of(null);
      }
  }


  redirectUser(){
    if (this.authService.isUserLoggedId()){
      this.customNavigationService.navigate(['/donor-info']);
    } else {
      this.customNavigationService.navigate(['/main','login']);
    }
  }


  redirectForLightCampaing(campaignInfo: CampaignInfo){
    if (campaignInfo.liteCampaignType === LiteCampaignTypes.Kiosk && !this.kioskId){
      this.customNavigationService.navigate(['/main', 'campaign', campaignInfo.campaignGuid, 'select-kiosk']);
    } else if (campaignInfo.liteCampaignType === LiteCampaignTypes.National && !this.uwId){
      this.customNavigationService.navigate(['/main', 'campaign', campaignInfo.campaignGuid, 'select-uw']);
    }

  }

  isNumber(value: string | number): boolean
  {
     return ((value != null) &&
             (value !== '') &&
             !isNaN(Number(value.toString())));
  }


}
